'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  imageAlt: string;
  rating: number;
  text: string;
  puppyName: string;
}

interface TestimonialHighlightsProps {
  testimonials: Testimonial[];
}

const TestimonialHighlights = ({ testimonials }: TestimonialHighlightsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="mb-16">
        <div className="rounded-xl bg-muted/30 p-8 md:p-12">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 flex justify-center">
              <div className="h-20 w-20 rounded-full bg-muted"></div>
            </div>
            <div className="mb-4 h-6 w-3/4 mx-auto rounded bg-muted"></div>
            <div className="mb-2 h-4 w-1/2 mx-auto rounded bg-muted"></div>
          </div>
        </div>
      </section>
    );
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="mb-16">
      <div className="rounded-xl bg-muted/30 p-8 md:p-12">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 flex justify-center">
            <div className="h-20 w-20 overflow-hidden rounded-full shadow-warm">
              <AppImage
                src={currentTestimonial.image}
                alt={currentTestimonial.imageAlt}
                className="h-full w-full object-cover"
                width={80}
                height={80}
              />
            </div>
          </div>

          <div className="mb-4 flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Icon
                key={i}
                name="StarIcon"
                size={20}
                variant={i < currentTestimonial.rating ? 'solid' : 'outline'}
                className={i < currentTestimonial.rating ? 'text-accent' : 'text-muted-foreground'}
              />
            ))}
          </div>

          <blockquote className="mb-6 font-body text-lg italic leading-relaxed text-foreground">
            &ldquo;{currentTestimonial.text}&rdquo;
          </blockquote>

          <div className="mb-2 font-heading text-xl font-semibold text-foreground">
            {currentTestimonial.name}
          </div>
          <div className="mb-1 text-sm text-muted-foreground">
            {currentTestimonial.location}
          </div>
          <div className="text-sm font-medium text-secondary">
            Proud owner of {currentTestimonial.puppyName}
          </div>

          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={handlePrev}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-card text-foreground shadow-warm transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeftIcon" size={20} />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-smooth ${
                    index === currentIndex
                      ? 'w-8 bg-primary' :'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-card text-foreground shadow-warm transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRightIcon" size={20} />
            </button>
          </div>

          <div className="mt-8">
            <Link
              href="/customer-testimonials"
              className="inline-flex items-center gap-2 font-medium text-primary transition-smooth hover:text-secondary"
            >
              Read All Testimonials
              <Icon name="ArrowRightIcon" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialHighlights;